// BCP関連の型定義
import React from 'react';

export interface BCPFormData {
  // 簡易版-1. 基本情報と事業継続方針の策定
  companyInfo: string;
  continuityPolicy: string;
  
  // 簡易版-2. 想定リスクと影響分析
  riskAnalysis: string;

  // 簡易版-3. 重要業務の特定と復旧計画
  criticalBusinesses: string;
  recoveryPlan: string;
  financialPlan: string;
  stockpilePlan: string;

  // 簡易版-4. 現地復旧以外の代替手段
  alternativeMeasures: string;
  
  // 簡易版-5. BCP発動基準
  activationCriteria: string;
  
  // 簡易版-6. 緊急時の組織体制
  emergencyOrganization: string;
  rolesResponsibilities: string;
  communicationMethods: string;
  
  // 簡易版-7. 事業継続に向けたその他の取組みなど
  regularTraining: string;
  bcpReview: string;
  employeeEducation: string;
}

export interface BCPGenerationRequest {
  files: File[];
  companyData?: Partial<BCPFormData>;
}

export interface BCPGenerationResponse {
  success: boolean;
  data?: BCPFormData;
  error?: string;
  processingTime?: number;
}

export interface BCPValidationError {
  field: keyof BCPFormData;
  message: string;
}

export interface BCPValidationResult {
  isValid: boolean;
  errors: BCPValidationError[];
}

export interface BCPExportOptions {
  format: 'pdf' | 'docx' | 'excel';
  includeGuidance: boolean;
  template?: string;
}

export interface BCPSection {
  id: keyof BCPFormData;
  title: string;
  description: string;
  required: boolean;
  placeholder: string;
  minLength?: number;
  maxLength?: number;
  minHeight?: 'small' | 'medium' | 'large';
  readOnly?: boolean;
  guide?: {
    content: React.ReactNode;
    example?: React.ReactNode;
  };
}

// BCP Management Types (based on backend models)

export type BCPType = 'introductory' | 'simple' | 'basic';
export type BCPStatus = 'draft' | 'published' | 'archived';

export interface BCPContent {
  title: string;
  sections: Record<string, any>;
}

export interface BCPGenerationMetadata {
  generated_at?: string;
  model?: string;
  [key: string]: any;
}

export interface BCPListItem {
  id: string;
  tenant_id: string;
  type: BCPType;
  status: BCPStatus;
  category?: string;
  tags?: string[];
  created_at: string;
  updated_at: string;
  created_by?: string;
  // Extended fields for better list display
  title?: string;
  description?: string;
  company_name?: string;
  last_accessed?: string;
  completion_rate?: number;
  version_count?: number;
}

export interface BCPDocument {
  id: string;
  tenant_id: string;
  type: BCPType;
  content: BCPContent;
  form_data?: BCPFormData;
  generation_metadata?: BCPGenerationMetadata;
  status: BCPStatus;
  tags?: string[];
  category?: string;
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
}

export interface BCPSaveRequest {
  type: BCPType;
  content: BCPContent;
  form_data?: BCPFormData;
  generation_metadata?: BCPGenerationMetadata;
  tags?: string[];
  category?: string;
  status?: BCPStatus;
  tenant_id?: string;
}

export interface BCPUpdateRequest {
  type?: BCPType;
  content?: BCPContent;
  form_data?: BCPFormData;
  tags?: string[];
  category?: string;
  status?: BCPStatus;
  change_summary?: string;
}

export interface BCPListQuery {
  tenant_id?: string;
  type?: BCPType;
  status?: BCPStatus;
  category?: string;
  tags?: string[];
  search?: string;
  limit?: number;
  offset?: number;
  sort_by?: 'type' | 'created_at' | 'updated_at' | 'status';
  sort_order?: 'asc' | 'desc';
}

export interface BCPListResponse {
  items: BCPListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface BCPVersion {
  id: string;
  version_number: number;
  version_name?: string;
  content: BCPContent;
  created_at: string;
  created_by?: string;
  change_summary?: string;
}

// UI Helper Types

export interface BCPFilter {
  type?: BCPType;
  status?: BCPStatus;
  category?: string;
  search?: string;
}

export interface BCPTableColumn {
  key: keyof BCPListItem | 'actions';
  label: string;
  sortable?: boolean;
  width?: string;
}

export const BCP_TYPE_LABELS: Record<BCPType, string> = {
  'introductory': '超入門',
  'simple': '簡易版',
  'basic': '基本版',
};

export const BCP_STATUS_LABELS: Record<BCPStatus, string> = {
  'draft': '下書き',
  'published': '公開済み',
  'archived': 'アーカイブ',
};

export const BCP_TABLE_COLUMNS: BCPTableColumn[] = [
  { key: 'type', label: 'BCP種別', sortable: true, width: '15%' },
  { key: 'status', label: 'ステータス', sortable: true, width: '12%' },
  { key: 'category', label: 'カテゴリ', sortable: false, width: '15%' },
  { key: 'created_at', label: '作成日', sortable: true, width: '15%' },
  { key: 'updated_at', label: '最終更新日', sortable: true, width: '15%' },
  { key: 'actions', label: '操作', sortable: false, width: '28%' },
];
